import { View, TouchableOpacity } from "react-native"
import CircleDot from "@/assets/icons/circle-dot.svg"
import { type ReactNode } from "react"
import BFText, { H6 } from "@/components/standard/BFText"
import { type SortDirectionOption, type CompSortOrder } from "@/types"
import { cn } from "@/components/util/cn"

interface SectionHeaderProps {
  children: string
}

const SectionHeader = ({ children }: SectionHeaderProps) => (
  <View className="flex h-[24px] bg-grey justify-center border-b border-light-grey">
    <BFText className="text-[10px] text-midnight-blue font-spacegrotesk pl-2">
      {children}
    </BFText>
  </View>
)

interface SectionItemProps {
  label: string
  selected?: boolean
  onSelect: () => void
  arrowIcon?: ReactNode
}

const SectionItem = ({
  label,
  selected,
  onSelect,
  arrowIcon,
}: SectionItemProps) => {
  return (
    <View className="flex flex-1 border-b border-light-grey">
      <TouchableOpacity
        testID={`section-item-${label}`}
        className="flex flex-1"
        onPress={onSelect}
      >
        <View className="flex flex-row h-[33px] items-center">
          <View
            className={`flex w-[14px] ml-3 ${selected ? "visible" : "invisible"}`}
          >
            <CircleDot width={6} height={6} />
          </View>
          <H6 className={`${selected ? "text-calm-blue" : "text-dark-grey"}`}>
            {label}
          </H6>
          {arrowIcon && <View className="ml-2">{arrowIcon}</View>}
        </View>
      </TouchableOpacity>
    </View>
  )
}

export interface SortOptionConfig {
  label: string | null
  value: string | null // This can be a sorting criterion or direction
  arrowIcon?: ReactNode
  defaultDirection?: SortDirectionOption
}

export interface SortSectionConfig {
  header: string
  options: SortOptionConfig[]
}

export interface ReusableSortPopupProps {
  displays: boolean
  sort: string | null // This should match the criterion's value (e.g., "distance")
  direction: SortDirectionOption | CompSortOrder
  isReversedByDefault?: boolean
  sections: SortSectionConfig[]
  onSelectSort: (sort: string, label: string) => void
  onSelectDirection: (
    direction: SortDirectionOption | CompSortOrder,
    label: string
  ) => void
  onClose: () => void
  positionClassName?: string // For per screen positioning
}

export default function ReusableSortPopup({
  displays,
  sort,
  direction,
  isReversedByDefault = false,
  sections,
  onSelectSort,
  onSelectDirection,
  onClose,
  positionClassName = "",
}: ReusableSortPopupProps) {
  if (!displays) return null

  return (
    <>
      {/* Invisible overlay that closes the popup when tapped. Nativewind couldn't handle this. */}
      <TouchableOpacity
        testID={`sort-popup-invisible-underlay`}
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          opacity: 0,
        }}
        activeOpacity={1}
        onPress={onClose}
      />

      {/* Popup content */}
      <View
        className={cn(
          positionClassName,
          "absolute z-20 w-[180px] flex flex-col rounded-[2px] border border-light-grey bg-[#fcfcfc]"
        )}
        style={{
          shadowColor: "#000",
          shadowOffset: { width: 1, height: 1 },
          shadowOpacity: 0.15,
          shadowRadius: 1.5,
          elevation: 1.5,
        }}
      >
        {sections.map((section, sectionIndex) => (
          <View key={sectionIndex}>
            <SectionHeader>{section.header}</SectionHeader>
            {section.options
              .filter(
                (option): option is { label: string; value: string } =>
                  option.label !== null && option.value !== null
              )
              .map((option, optionIndex) => (
                <SectionItem
                  key={optionIndex}
                  label={option.label}
                  selected={
                    section.header === "ORDERING"
                      ? direction === option.value
                      : sort === option.value
                  }
                  onSelect={() => {
                    if (section.header === "ORDERING") {
                      onSelectDirection(
                        option.value as CompSortOrder,
                        option.label
                      )
                    } else {
                      onSelectSort(option.value, option.label)
                    }
                    onClose()
                  }}
                />
              ))}
          </View>
        ))}
      </View>
    </>
  )
}
